import { connect } from 'react-redux';
import { makeStyles, tokens } from '@fluentui/react-components';
import { AppState } from '../../../../redux/reducers';
import { QueueDisplayScreen, CrearLlamadoRequest } from '../../../../models/Entities/Turnero';
import * as QueueDisplayScreenStore from '../../../../redux/middleware/Turnero/Turnero.QueueDisplayScreen.Middleware';
import * as LlamadosStore from '../../../../redux/middleware/Turnero/Turnero.Llamados.Middleware';
import { ErrorType } from '../../../../models/HttpError';

export interface OwnProps {
  sectorId?: number;
}

interface StateProps {
  displays: QueueDisplayScreen[];
  isLoading: boolean;
  failOnLoading: boolean;
  error?: ErrorType;
}

interface DispatchProps {
  GetQueueDisplayScreens: typeof QueueDisplayScreenStore.actionCreators.GetQueueDisplayScreens;
  CrearLlamado: (payload: CrearLlamadoRequest) => void;
  DeleteQueueDisplayScreen: (id: number) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = (state: AppState, _ownProps: OwnProps): StateProps => ({
  displays: state.Turnero?.QueueDisplayScreen?.list || [],
  isLoading: state.Turnero?.QueueDisplayScreen?.isLoadingAll || false,
  failOnLoading: state.Turnero?.QueueDisplayScreen?.failOnLoadingAll || false,
  error: state.Turnero?.QueueDisplayScreen?.error,
});

export const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  GetQueueDisplayScreens: () => dispatch(QueueDisplayScreenStore.actionCreators.GetQueueDisplayScreens()),
  CrearLlamado: (payload: CrearLlamadoRequest) => dispatch(LlamadosStore.actionCreators.CrearLlamado(payload)),
  DeleteQueueDisplayScreen: (id: number) => dispatch(QueueDisplayScreenStore.actionCreators.DeleteQueueDisplayScreen(id)),
});

export const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    position: 'relative',
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalM,
    paddingLeft: `calc(${tokens.spacingHorizontalM} + 4px)`,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minHeight: '9rem',
    cursor: 'pointer',
    transitionProperty: 'border-color, box-shadow',
    transitionDuration: '0.15s',
    ':hover': {
      boxShadow: tokens.shadow4,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  cardActive: {
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      width: '4px',
      borderTopLeftRadius: tokens.borderRadiusMedium,
      borderBottomLeftRadius: tokens.borderRadiusMedium,
      backgroundColor: '#16a34a',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    flexShrink: 0,
  },
  icon: {
    fontSize: '18px',
    width: '18px',
    height: '18px',
    color: tokens.colorBrandForeground1,
  },
  name: {
    fontWeight: 600,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.4',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  regNumber: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    letterSpacing: '0.04em',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
  },
  cardActions: {
    display: 'flex',
    gap: '4px',
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    padding: '2px 4px',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '40px 16px',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  error: {
    padding: tokens.spacingHorizontalL,
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: tokens.borderRadiusMedium,
  },
});

export const connector = connect<StateProps, DispatchProps, OwnProps, AppState>(mapStateToProps, mapDispatchToProps);
